// Observations de signos vitales a partir de las tomas escritas en los formularios de texto (002, 003, 119, 122). Mismo modelo
// que 020/058 (perfil vital-signs, LOINC del catálogo, PA como un solo Observation con dos `component`), para que las
// constantes que hoy solo iban al PDF aparezcan también en Signos Vitales. Solo se crea lo que se pueda leer como número:
// un texto libre ("normal", "36 aprox") se queda en el PDF firmado y no genera un Observation inventado. Glucosa,
// hemoglobina capilar, perímetros e IMC no se crean (sin código LOINC verificado; el IMC se calcula de peso y talla).
import type { Encounter, Observation, Patient, Reference } from '@medplum/fhirtypes';
import { PA_DIASTOLICA_LOINC, PA_PANEL_LOINC, PA_SISTOLICA_LOINC, signoVitalPorClave, VITAL_SIGNS_CATEGORY } from './vitalSigns';

export interface TomaSignos {
  /** aaaa-mm-dd — si falta, la fecha de hoy. */
  readonly fecha?: string;
  /** hh:mm — si falta (o es inválida), el momento del guardado. */
  readonly hora?: string;
  readonly temperatura?: string;
  readonly fc?: string;
  readonly fr?: string;
  readonly spo2?: string;
  readonly peso?: string;
  readonly talla?: string;
  /** "120/80". */
  readonly presion?: string;
  /** Solo 122: antes / durante / después de la transfusión. */
  readonly momento?: string;
}

export const EXTENSION_MOMENTO_TRANSFUSION = 'http://logiacare.ec/fhir/StructureDefinition/momento-transfusion';

export function aNumero(texto: string | undefined): number | undefined {
  const limpio = (texto ?? '').trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(limpio)) return undefined;
  const n = Number(limpio);
  return Number.isFinite(n) ? n : undefined;
}

export function aPresion(texto: string | undefined): { sistolica: number; diastolica: number } | undefined {
  const m = /^\s*(\d{2,3})\s*[/-]\s*(\d{2,3})\s*$/.exec(texto ?? '');
  return m ? { sistolica: Number(m[1]), diastolica: Number(m[2]) } : undefined;
}

/** aaaa-mm-dd del día LOCAL de un instante ISO: la hora escrita en el formulario es local, así que el día también (el slice UTC adelantaba el día tras las 19:00 en Ecuador). */
export function diaLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function momentoDe(toma: TomaSignos, ahoraIso: string): string {
  if (toma.hora && /^\d{2}:\d{2}$/.test(toma.hora)) {
    const dia = toma.fecha && /^\d{4}-\d{2}-\d{2}$/.test(toma.fecha) ? toma.fecha : diaLocal(ahoraIso);
    const d = new Date(`${dia}T${toma.hora}:00`);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return ahoraIso;
}

const CLAVE_POR_CAMPO = { temperatura: 'temperatura', fc: 'fc', fr: 'fr', spo2: 'sato2', peso: 'peso', talla: 'talla' } as const;

export function observacionesDeToma(toma: TomaSignos, subject: Reference<Patient>, encounter: Reference<Encounter> | undefined, ahoraIso: string): Observation[] {
  const efectivo = momentoDe(toma, ahoraIso);
  const extension = toma.momento?.trim() ? [{ url: EXTENSION_MOMENTO_TRANSFUSION, valueString: toma.momento.trim() }] : undefined;
  const resultado: Observation[] = [];
  for (const [campo, clave] of Object.entries(CLAVE_POR_CAMPO) as [keyof typeof CLAVE_POR_CAMPO, string][]) {
    const valor = aNumero(toma[campo]);
    if (valor === undefined) continue;
    const signo = signoVitalPorClave(clave);
    resultado.push({
      resourceType: 'Observation',
      status: 'final',
      category: [VITAL_SIGNS_CATEGORY],
      code: { coding: [{ system: 'http://loinc.org', code: signo.loinc, display: signo.etiqueta }] },
      subject,
      encounter,
      effectiveDateTime: efectivo,
      valueQuantity: { value: valor, unit: signo.unidad, system: 'http://unitsofmeasure.org', code: signo.unidadUcum },
      extension,
    });
  }
  const pa = aPresion(toma.presion);
  if (pa) {
    resultado.push({
      resourceType: 'Observation',
      status: 'final',
      category: [VITAL_SIGNS_CATEGORY],
      code: { coding: [{ system: 'http://loinc.org', code: PA_PANEL_LOINC, display: 'Presión arterial' }] },
      subject,
      encounter,
      effectiveDateTime: efectivo,
      component: [
        { code: { coding: [{ system: 'http://loinc.org', code: PA_SISTOLICA_LOINC, display: 'Sistólica' }] }, valueQuantity: { value: pa.sistolica, unit: 'mmHg', system: 'http://unitsofmeasure.org', code: 'mm[Hg]' } },
        { code: { coding: [{ system: 'http://loinc.org', code: PA_DIASTOLICA_LOINC, display: 'Diastólica' }] }, valueQuantity: { value: pa.diastolica, unit: 'mmHg', system: 'http://unitsofmeasure.org', code: 'mm[Hg]' } },
      ],
      extension,
    });
  }
  return resultado;
}

export const observacionesDeTomas = (tomas: readonly TomaSignos[], subject: Reference<Patient>, encounter: Reference<Encounter> | undefined, ahoraIso: string): Observation[] =>
  tomas.flatMap((t) => observacionesDeToma(t, subject, encounter, ahoraIso));
